import { Icon } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiEye,
  mdiLoading,
  mdiTrashCan,
} from "@mdi/js";
import { TabContext, TabPanel } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
} from "@mui/material";
import { green, red } from "@mui/material/colors";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CreatePurchaseOrderByQuotation } from "../../../../../@types/services/CreatePurchaseOrderByQuotation";
import type { CreatePurchaseOrderByQuotationExecution } from "../../../../../@types/services/CreatePurchaseOrderByQuotationExecution";
import type { MaintenanceWindow } from "../../../../../@types/services/MaintenanceWindow";
import {
  listQuotations,
  removeCreatePurchaseOrderByQuotation,
} from "../../../../../__generated__/orval/endpoints/quotations/quotations";
import type { EntitiesQuotationCollectionMain } from "../../../../../__generated__/orval/models/entitiesQuotationCollectionMain.zod";
import { CommonErrorHelp } from "../../../../../components/molecules/CommonErrorHelp";
import { ApiErrorsTable } from "../../../../../components/organisms/ApiErrorsTable";
import {
  generateCreatePurchaseOrderByQuotation,
  getCreatePurchaseOrderByQuotation,
} from "../../../../../services/createPurchaseOrderByQuotations";
import { getMicrosoftToken } from "../../../../../services/getMicrosoftToken";
import { fetchMaintenanceWindows } from "../../../../../services/maintenanceWindows";
import { i18nextParseToDate } from "../../../../../utils/i18nextParseTo";

type ExecutionRowProps = {
  execution: CreatePurchaseOrderByQuotationExecution;
};

const ExecutionRow: React.FC<ExecutionRowProps> = ({ execution }) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow
        key={execution.id}
        sx={{
          "& > *": { borderBottom: "unset" },
          borderLeftColor: execution.success ? green["500"] : red["500"],
          borderLeftStyle: "solid",
        }}
      >
        <TableCell>
          {Object.keys(execution.api_errors).length !== 0 && (
            <IconButton
              aria-label="expand row"
              onClick={() => setOpen(!open)}
              size="small"
            >
              {open ? (
                <Icon icon={mdiChevronUp} />
              ) : (
                <Icon icon={mdiChevronDown} />
              )}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          {t("intlDateTime", i18nextParseToDate(execution.created_at))}
        </TableCell>

        <TableCell>
          {t("intlDateTime", i18nextParseToDate(execution.updated_at))}
        </TableCell>

        <TableCell align="center">
          {execution.completed ? t("yes") : t("no")}
        </TableCell>

        <TableCell align="center">
          {execution.success ? t("yes") : t("no")}
        </TableCell>

        <TableCell align="center">
          {Object.keys(execution.api_errors).length !== 0 ? t("yes") : t("no")}
        </TableCell>
      </TableRow>
      {Object.keys(execution.api_errors).length !== 0 && (
        <TableRow>
          <TableCell colSpan={6} style={{ paddingBottom: 0, paddingTop: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <ApiErrorsTable apiErrors={execution.api_errors} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

type QuotationRowProps = {
  quotationId: number;
  createPurchaseOrderByQuotation: CreatePurchaseOrderByQuotation;
  lastExecution?: CreatePurchaseOrderByQuotationExecution;
  tableRefreshCallback: React.Dispatch<React.SetStateAction<boolean>>;
};

const QuotationRow: React.FC<QuotationRowProps> = ({
  quotationId,
  createPurchaseOrderByQuotation,
  lastExecution,
  tableRefreshCallback,
}) => {
  const { t } = useTranslation();

  const [quotation, setQuotation] = useState<EntitiesQuotationCollectionMain>();
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loadingRemove, setLoadingRemove] = useState(false);

  const columnQuantity = useMemo(() => 7, []);

  const handleFetchQuotation = useCallback((id: number): void => {
    setLoading(() => true);
    listQuotations({ id: [id], items_per_page: 1 })
      .then((data) => {
        setQuotation(() => data.results[0]);
      })
      .finally(() => {
        setLoading(() => false);
      });
  }, []);

  const handleRemoveQuotation = useCallback(
    (id: number): void => {
      setLoadingRemove(() => true);
      removeCreatePurchaseOrderByQuotation(id)
        .then(() => {
          tableRefreshCallback(() => true);
        })
        .finally(() => {
          setLoadingRemove(() => false);
          setShowConfirm(() => false);
        });
    },
    [tableRefreshCallback],
  );

  useEffect(() => {
    handleFetchQuotation(quotationId);
  }, [quotationId, handleFetchQuotation]);

  if (loading) {
    return (
      <TableRow>
        {Array(columnQuantity).map(() => (
          <TableCell key={`skeleton-cell-${crypto.randomUUID()}`}>
            <Skeleton />
          </TableCell>
        ))}
      </TableRow>
    );
  }

  if (!quotation) {
    return (
      <TableRow>
        <TableCell colSpan={columnQuantity}>Fail to load</TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell>{quotation.identifier}</TableCell>
      <TableCell>
        {!!quotation.pr_item.requisition_date &&
          t("intlDate", i18nextParseToDate(quotation.pr_item.requisition_date))}
      </TableCell>
      <TableCell>{quotation.material.matnumber}</TableCell>
      <TableCell>{quotation.vendor_winner?.name}</TableCell>
      <TableCell>
        {quotation.purchase_order_item?.purchase_order.po_number}
      </TableCell>
      <TableCell>{quotation.purchase_order_item?.po_item_number}</TableCell>

      <TableCell align="right">
        {!quotation.purchase_order_item &&
          (quotation.create_purchase_order_item_jid ? (
            <Tooltip
              title={t(
                "quotation.info.purchase_order_item_relation_in_progress",
              )}
            >
              <Icon icon={mdiLoading} spin />
            </Tooltip>
          ) : (
            !createPurchaseOrderByQuotation.completed &&
            (showConfirm ? (
              <Tooltip title={t("confirm")}>
                <Button
                  color="error"
                  loading={loadingRemove}
                  loadingPosition="center"
                  onClick={() => handleRemoveQuotation(quotation.id)}
                >
                  {t("confirm")}
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title={t("remove")}>
                <IconButton
                  disabled={lastExecution && !lastExecution.completed}
                  onClick={() => setShowConfirm(() => true)}
                >
                  <Icon icon={mdiTrashCan} />
                </IconButton>
              </Tooltip>
            ))
          ))}
        <Tooltip title={t("view")}>
          <IconButton
            component={Link}
            // to={`/vbuyer/quotations/${quotation.id}`}
            href={`/vbuyer/quotations/${quotation.id}`}
            target="_blank"
          >
            <Icon icon={mdiEye} />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

const ShowCreatePurchaseOrderByCreatePurchaseOrderByQuotation: React.FC =
  () => {
    const { createPurchaseOrderByQuotationId } = Route.useParams();

    const { t } = useTranslation();

    const [maintenanceWindows, setMaintenanceWindows] = useState<
      MaintenanceWindow[]
    >([]);
    const [createPurchaseOrderByQuotation, setCreatePurchaseOrderByQuotation] =
      useState<CreatePurchaseOrderByQuotation>();
    const [notFound, setNotFound] = useState(false);
    const [loading, setLoading] = useState(true);
    const [tabOpen, setTabOpen] = useState("quotations");
    const [refresh, setRefresh] = useState(true);
    const [loadingNewExecution, setLoadingNewExecution] = useState(false);
    const [lastExecution, setLastExecution] = useState<
      undefined | CreatePurchaseOrderByQuotationExecution
    >();

    const didInit = useRef(false);

    const hasMaintenanceWindows = maintenanceWindows.length > 0;

    const handleRunAgain = async (): Promise<void> => {
      setLoadingNewExecution(true);
      try {
        const authenticated = await getMicrosoftToken("procurement");
        if (!authenticated) {
          return;
        }
        await generateCreatePurchaseOrderByQuotation(
          createPurchaseOrderByQuotationId,
        );
        setRefresh(true);
      } catch (error) {
        if (
          axios.isAxiosError(error) &&
          error.response?.status === 401 &&
          error.response.data?.auth_url
        ) {
          window.location.href = error.response.data.auth_url;
          return;
        }
        throw error;
      } finally {
        setLoadingNewExecution(false);
      }
    };

    const handleFetchCreatePurchaseOrderByQuotation = useCallback(
      async (id: string) => {
        setLoading(() => true);
        await getCreatePurchaseOrderByQuotation(id)
          .then((resource) => {
            const temp = resource;
            temp.create_purchase_order_by_quotation_executions =
              resource.create_purchase_order_by_quotation_executions.sort(
                (a, b) =>
                  new Date(b.created_at).getTime() -
                  new Date(a.created_at).getTime(),
              );
            setCreatePurchaseOrderByQuotation(() => resource);
          })
          .catch(() => {
            setNotFound(() => true);
          })
          .finally(() => {
            setRefresh(() => false);
            setLoading(() => false);
          });
      },
      [],
    );

    const handleTabChange = (
      _event: React.SyntheticEvent,
      newValue: string,
    ): void => {
      setTabOpen(() => newValue);
    };

    const handleFetchMaintenanceWindows = useCallback(async () => {
      const response = await fetchMaintenanceWindows({
        filters: { active: true, system: "sap_p03" },
      });
      setMaintenanceWindows(
        response.results.filter(
          (maintenanceWindow) =>
            maintenanceWindow.blocked_actions.length === 0 ||
            maintenanceWindow.blocked_actions.includes(
              "issue_material_purchase_order",
            ),
        ),
      );
    }, []);

    useEffect(() => {
      if (!didInit.current) {
        didInit.current = true;
        handleFetchMaintenanceWindows();
      }
    }, [handleFetchMaintenanceWindows]);

    useEffect(() => {
      if (!refresh) {
        return;
      }

      if (!createPurchaseOrderByQuotationId) {
        setNotFound(() => true);
        return;
      }

      handleFetchCreatePurchaseOrderByQuotation(
        createPurchaseOrderByQuotationId,
      );
    }, [
      handleFetchCreatePurchaseOrderByQuotation,
      createPurchaseOrderByQuotationId,
      refresh,
    ]);

    useEffect(() => {
      if (
        !createPurchaseOrderByQuotation ||
        createPurchaseOrderByQuotation
          .create_purchase_order_by_quotation_executions.length <= 0
      ) {
        return;
      }

      setLastExecution(
        () =>
          createPurchaseOrderByQuotation
            .create_purchase_order_by_quotation_executions[0],
      );
    }, [createPurchaseOrderByQuotation]);

    if (notFound || !createPurchaseOrderByQuotationId) {
      return <Navigate to="/not_found" />;
    }

    return (
      <Box
        component="main"
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          height: "100%",
          justifyContent: "center",
          px: 2,
          width: "100%",
        }}
      >
        <h1>{t("createPurchaseOrderByQuotation.title")}</h1>

        {hasMaintenanceWindows &&
          maintenanceWindows.map((maintenanceWindow) => (
            <Alert key={maintenanceWindow.id} severity="warning" sx={{ mb: 2 }}>
              {maintenanceWindow.message}
            </Alert>
          ))}

        {createPurchaseOrderByQuotation &&
          !createPurchaseOrderByQuotation.completed && (
            <Button
              disabled={
                loadingNewExecution ||
                (lastExecution &&
                  (!lastExecution.completed ||
                    !createPurchaseOrderByQuotation.can_retry)) ||
                hasMaintenanceWindows
              }
              loading={loadingNewExecution}
              loadingPosition="start"
              onClick={handleRunAgain}
              type="button"
              variant="outlined"
            >
              {t("createPurchaseOrderByQuotation.runAgain")}
            </Button>
          )}

        <TabContext value={tabOpen}>
          <Box
            sx={{
              alignItems: "center",
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Tabs onChange={handleTabChange} value={tabOpen}>
              <Tab
                label={t("createPurchaseOrderByQuotation.quotations")}
                value="quotations"
              />
              <Tab
                label={t("createPurchaseOrderByQuotation.executions")}
                value="executions"
              />
            </Tabs>
            <CommonErrorHelp errorType="create_purchase_order_material_quotation" />
          </Box>

          <TabPanel value="quotations">
            <Box>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">
                        {t("quotation.identifier")}
                      </TableCell>
                      <TableCell align="left">
                        {t("quotation.requisitionDate")}
                      </TableCell>
                      <TableCell align="left">
                        {t("quotation.material")}
                      </TableCell>
                      <TableCell align="left">
                        {t("quotation.vendor")}
                      </TableCell>
                      <TableCell align="left">Purchase Order</TableCell>
                      <TableCell align="left">Purchase Order Item</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!!loading && (
                      <TableRow key="loading-skeleton">
                        {Array(7).map(() => (
                          <TableCell
                            key={`skeleton-cell-${crypto.randomUUID()}`}
                          >
                            <Skeleton />
                          </TableCell>
                        ))}
                      </TableRow>
                    )}
                    {!loading &&
                      createPurchaseOrderByQuotation &&
                      createPurchaseOrderByQuotation.quotations?.length > 0 &&
                      createPurchaseOrderByQuotation.quotations.map(
                        (quotation) => (
                          <QuotationRow
                            createPurchaseOrderByQuotation={
                              createPurchaseOrderByQuotation
                            }
                            key={quotation.id}
                            lastExecution={lastExecution}
                            quotationId={quotation.id}
                            tableRefreshCallback={setRefresh}
                          />
                        ),
                      )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>

          <TabPanel value="executions">
            <Box>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell align="left">
                        {t("createPurchaseOrderByQuotationExecution.createdAt")}
                      </TableCell>
                      <TableCell align="left">
                        {t("createPurchaseOrderByQuotationExecution.updatedAt")}
                      </TableCell>
                      <TableCell align="center">
                        {t("createPurchaseOrderByQuotationExecution.completed")}
                      </TableCell>
                      <TableCell align="center">
                        {t("createPurchaseOrderByQuotationExecution.success")}
                      </TableCell>
                      <TableCell align="center">
                        {t("createPurchaseOrderByQuotationExecution.hasErrors")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!!loading && (
                      <TableRow key="loading-skeleton">
                        {Array(6).map(() => (
                          <TableCell
                            key={`skeleton-cell-${crypto.randomUUID()}`}
                          >
                            <Skeleton />
                          </TableCell>
                        ))}
                      </TableRow>
                    )}
                    {!loading &&
                      createPurchaseOrderByQuotation &&
                      createPurchaseOrderByQuotation
                        .create_purchase_order_by_quotation_executions?.length >
                        0 &&
                      createPurchaseOrderByQuotation.create_purchase_order_by_quotation_executions.map(
                        (execution) => (
                          <ExecutionRow
                            execution={execution}
                            key={execution.id}
                          />
                        ),
                      )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>
        </TabContext>
      </Box>
    );
  };

export const Route = createFileRoute(
  "/_private/vbuyer/create_purchase_order_by_quotations/$createPurchaseOrderByQuotationId/",
)({
  component: ShowCreatePurchaseOrderByCreatePurchaseOrderByQuotation,
});
